import { useNotification } from "@itandi/itandi-bb-ui/NotificationBarStore";
import Link from "next/link";
import type { ComponentProps } from "react";
import { useState } from "react";
import { DefaultV2 } from "~/components/common/layouts/DefaultV2";
import { ClientDealLayout } from "~/components/common/layouts/deals/ClientDealLayout";
import Loading from "~/components/common/parts/loading/Loading";
import { ReportImagesArea } from "~/components/common/reports/ReportImagesArea";
import { ReportImagesAreaSp } from "~/components/common/reports/ReportImagesAreaSp";
import { ReportImageUploadPanel } from "~/components/common/reports/ReportImageUploadPanel";
import { useDealCompletionReportImages } from "~/hooks/deals/useDealCompletionReportImages";
import { useDeals } from "~/hooks/useDeals";
import { useIdQueryHandler } from "~/hooks/useIdQueryHandler";
import type { NskResponse } from "~/models/Api";
import type {
	DealCompletionReportImage,
	DealReportBulkUpdateParams,
	DealReportImage
} from "~/models/DealReportImage";
import {
	NUMBER_OF_DELETE_AT_ONCE,
	NUMBER_OF_UPLOAD_AT_ONCE
} from "~/models/DealReportImage";
import { PATH as DEALS_PATH } from "~/pages/deals";
import { PATH } from "~/pages/deals/[id]";
import { isSuccess } from "~/utils/api/api_handler";
import { getDownloadPath as getDownloadPdfPath } from "~/utils/api/deals/DealCompletionReport";
import {
	bulkDelete,
	bulkUpdate,
	destroy,
	getDownloadPath as getDownloadImagePath,
	getDownloadZipPath,
	create as postCompletionReportImages,
	update,
	updateSeq
} from "~/utils/api/deals/DealCompletionReportImage";
import Styles from "./Page.module.scss";

export function Page() {
	const { notify } = useNotification();
	const id = useIdQueryHandler();
	const [uploading, setUploading] = useState(false);
	const [deleting, setDeleting] = useState(false);
	const [processedCount, setProcessedCount] = useState(0);
	const [totalCount, setTotalCount] = useState(0);
	const [selectedImageIds, setSelectedImageIds] = useState<readonly number[]>(
		[]
	);
	const { data: deal } = useDeals().show(id);
	// globalMutateしたい
	const { data: reportImages, mutate: mutateReportImages } =
		useDealCompletionReportImages().index(id);
	if (deal === null || reportImages === null) {
		return <Loading isLoading />;
	}
	const items = [
		{ as: Link, href: DEALS_PATH, label: "案件一覧" },
		{ as: Link, href: PATH(deal.id), label: "案件詳細" },
		{ label: "完了報告画像" }
	] as const satisfies ComponentProps<typeof ClientDealLayout>["items"];
	const handleDrop = async (files: File[]): Promise<void> => {
		const total = files.length;
		setProcessedCount(0);
		setTotalCount(total);
		setUploading(true);
		const arrFiles: File[][] = [];
		let finishedFiles = 0;
		while (files.length > 0) {
			arrFiles.push(files.splice(0, NUMBER_OF_UPLOAD_AT_ONCE));
		}
		for (const sendFiles of arrFiles) {
			const result = await postCompletionReportImages(sendFiles, deal.id);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
				return;
			}
			finishedFiles += sendFiles.length;
			setProcessedCount(finishedFiles);
			await mutateReportImages();
		}
		setUploading(false);
		notify({ content: "画像を登録しました。", variant: "success" });
	};
	const handleDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, undefined);
		window.open(path);
	};
	const handleDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id);
		window.open(path);
	};
	const handleSelectedDownloadZip = (): void => {
		const path = getDownloadZipPath(deal.id, selectedImageIds);
		window.open(path);
	};
	const handleSelectedDownloadPdf = (imagesPerPage: number): void => {
		const path = getDownloadPdfPath(deal.id, imagesPerPage, selectedImageIds);
		window.open(path);
	};
	const handleDownloadImage = (reportImage: DealReportImage): void => {
		const path = getDownloadImagePath(reportImage);
		window.open(path);
	};
	const handleUpdate = async (
		submitValue: DealReportImage
	): Promise<NskResponse> => update(submitValue);
	const handleBulkUpdate = async (
		submitValue: DealReportBulkUpdateParams
	): Promise<void> => {
		const result = await bulkUpdate(deal.id, selectedImageIds, submitValue);
		if (isSuccess(result)) {
			await mutateReportImages();
			notify({ content: "更新しました。", variant: "success" });
		} else {
			notify({ content: "エラーが発生しました。", variant: "error" });
		}
	};
	const handleDestroy = async (
		reportImage: DealReportImage
	): Promise<NskResponse> => {
		const result = await destroy(reportImage);
		if (isSuccess(result)) {
			setSelectedImageIds((prev) => {
				const imageIndex = prev.indexOf(reportImage.id);

				return imageIndex === -1 ? prev : prev.toSpliced(imageIndex, 1);
			});
		}
		return result;
	};
	const handleBulkDelete = async (): Promise<void> => {
		if (selectedImageIds.length === 0) {
			return;
		}
		const total = selectedImageIds.length;
		setProcessedCount(0);
		setTotalCount(total);
		setDeleting(true);
		const imageIdArray: number[][] = [];
		let finishedFiles = 0;
		for (let i = 0, j = total; i < j; i += NUMBER_OF_DELETE_AT_ONCE) {
			imageIdArray.push(
				selectedImageIds.slice(i, i + NUMBER_OF_DELETE_AT_ONCE)
			);
		}
		for (const ids of imageIdArray) {
			const result = await bulkDelete(deal.id, ids);
			if (isSuccess(result)) {
				await mutateReportImages();
			} else {
				notify({ content: "エラーが発生しました。", variant: "error" });
				return;
			}
			finishedFiles += ids.length;
			setProcessedCount(finishedFiles);
			await mutateReportImages();
		}
		setDeleting(false);
		setSelectedImageIds([]);
		notify({ content: "画像を削除しました。", variant: "success" });
	};
	const handleUpdateSeq = async (
		newOrderItems: DealReportImage[]
	): Promise<void> => {
		await mutateReportImages(newOrderItems as DealCompletionReportImage[], {
			revalidate: false
		});
		await updateSeq(newOrderItems, Number(id));
	};
	const onChangeSelectedImage = (selectedImageId: number): void => {
		if (selectedImageIds.includes(selectedImageId)) {
			setSelectedImageIds(
				selectedImageIds.filter((imageId) => imageId !== selectedImageId)
			);
		} else {
			setSelectedImageIds(selectedImageIds.concat([selectedImageId]));
		}
	};
	const handleResetSelectedIds = (): void => {
		setSelectedImageIds([]);
	};
	const handleSelectAllImages = (): void => {
		setSelectedImageIds(reportImages.map((image: DealReportImage) => image.id));
	};
	return (
		<DefaultV2>
			<ClientDealLayout deal={deal} enableChat items={items} noBackground>
				<div className={Styles.MainWrapper}>
					<h2 className={Styles.ReportTitle}>完了報告画像の登録</h2>
					<ReportImageUploadPanel
						deleting={deleting}
						handleDrop={handleDrop}
						processedCount={processedCount}
						totalCount={totalCount}
						uploading={uploading}
					/>
					<ReportImagesArea
						companyType="company"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
					<ReportImagesAreaSp
						companyType="company"
						handleBulkDelete={handleBulkDelete}
						handleBulkUpdate={handleBulkUpdate}
						handleDestroy={handleDestroy}
						handleDownloadImage={handleDownloadImage}
						handleDownloadPdf={handleDownloadPdf}
						handleDownloadZip={handleDownloadZip}
						handleResetSelectedIds={handleResetSelectedIds}
						handleSelectAllImages={handleSelectAllImages}
						handleSelectedDownloadPdf={handleSelectedDownloadPdf}
						handleSelectedDownloadZip={handleSelectedDownloadZip}
						handleUpdate={handleUpdate}
						handleUpdateSeq={handleUpdateSeq}
						onChangeSelectedImage={onChangeSelectedImage}
						reportImages={reportImages}
						selectedImageIds={selectedImageIds}
					/>
				</div>
			</ClientDealLayout>
		</DefaultV2>
	);
}
